import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { cn, MMSCAN_UI_I18N } from '@mmscan/angular-ui/core';

/**
 * Horizontal progress bar. Without `value` it is indeterminate (animated; a static bar when
 * reduced motion is on). Replaces `p-progressbar`.
 *
 * ```html
 * <mmscan-progress-bar [value]="uploaded()" label="Uploading files" />
 * <mmscan-progress-bar />
 * ```
 */
@Component({
  selector: 'mmscan-progress-bar',
  template: `
    @if (value() === null) {
      <div
        class="absolute inset-y-0 left-0 w-2/5 rounded-full bg-primary animate-mmscan-progress motion-reduce:w-full motion-reduce:opacity-60"
      ></div>
    } @else {
      <div class="h-full rounded-full bg-primary transition-[width]" [style.width.%]="percent()"></div>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    role: 'progressbar',
    '[class]': 'classes()',
    '[attr.aria-label]': 'label() || i18n().loading',
    '[attr.aria-valuemin]': 'value() === null ? null : 0',
    '[attr.aria-valuemax]': 'value() === null ? null : 100',
    '[attr.aria-valuenow]': 'value() === null ? null : percent()',
  },
})
export class MmscanProgressBar {
  protected readonly i18n = inject(MMSCAN_UI_I18N);

  /** 0–100; `null` for indeterminate. */
  readonly value = input<number | null>(null);
  readonly label = input<string>();
  readonly size = input<'sm' | 'md'>('md');

  protected readonly percent = computed(() => Math.min(100, Math.max(0, Math.round(this.value() ?? 0))));
  protected readonly classes = computed(() =>
    cn('relative block w-full overflow-hidden rounded-full bg-primary/15', this.size() === 'sm' ? 'h-1' : 'h-2')
  );
}
